import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { devWarn } from '@/lib/logger';

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [darkMode, setDarkMode] = useState(() => {
    try {
      const stored = localStorage.getItem('mcx-dark-mode');
      if (stored !== null) return stored === 'true';
    } catch (err) {
      // localStorage unavailable (private mode, quota exceeded) — fall back to OS preference
      devWarn('[ThemeContext] localStorage read failed:', err);
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    try {
      localStorage.setItem('mcx-dark-mode', String(darkMode));
    } catch (err) {
      // localStorage write failed — DOM class is still applied, persistence just won't survive reload
      devWarn('[ThemeContext] localStorage write failed:', err);
    }
  }, [darkMode]);

  const value = useMemo(
    () => ({ darkMode, toggleDarkMode: () => setDarkMode(prev => !prev) }),
    [darkMode]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
